import {useSiretLookup} from "@mytour/platform";
import {type CompanyType, CompanyTypesWithCapital, type SiretLookupResult} from "@mytour/shared";
import {Field} from "@mytour/ui/form";
import {Splitter} from "@mytour/ui/layout";
import {useEffect, useRef, useState} from "react";
import type {UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {CompanyTypeSelect} from "./CompanyTypeSelect";
import {SiretField} from "./SiretField";
import {type SiretAutofillPhase, SiretLookupFeedback} from "./SiretLookupFeedback";
import type {Step1Form, Step1Props} from "./Step1";

const SIRET_DEBOUNCE_MS = 300;
const SIRET_FORMAT = /^\d{14}$/;
// How long the "company found" flash stays before the fields are revealed.
const SUCCESS_FLASH_MS = 1400;
/**
 * Pas de défaut non vide (#1316) : "" est la valeur "non rempli" utilisée pour
 * purger un auto-fill non repris — jamais "EI", qui serait lu comme un vrai
 * choix utilisateur.
 */
const DEFAULT_COMPANY_TYPE = "" as CompanyType;

/**
 * Feature-flag ON variant — SIRET-first guided auto-fill (#158). The flag is
 * already true by construction (the Step1 selector only renders this variant
 * when it is on), so nothing here re-checks it.
 *
 * The lookup drives a small state machine (see `useStep1SiretAutofill`): enter
 * the SIRET → search → a brief "found" flash → the pre-filled fields appear; or
 * a reassuring problem message with a "continue / enter manually" action.
 *
 * Everything specific to the feature lives in this file (the engine + helpers),
 * so retiring the flag is a single-file delete.
 */
export function Step1AutofillForm({hookForm, disabled}: Step1Props) {
  const {t: translate} = useTranslation();
  const {phase, matchedName, problemKind, acknowledgeProblem, markCompanyTypePicked} =
    useStep1SiretAutofill(hookForm);
  const companyType = hookForm.watch("companyType");

  return (
    <>
      <p style={{marginBottom: "8px"}}>
        <strong>{`Identité de l'entreprise`}</strong>
      </p>
      <SiretField hookForm={hookForm} disabled={disabled} />
      <SiretLookupFeedback
        phase={phase}
        matchedName={matchedName}
        problemKind={problemKind}
        onContinue={acknowledgeProblem}
      />
      {phase === "revealed" && (
        <>
          <Splitter>
            <CompanyTypeSelect hookForm={hookForm} onPicked={markCompanyTypePicked} />
            {CompanyTypesWithCapital.has(companyType) && (
              <Field
                required
                name="companyCapital"
                type="number"
                label={translate("myTourApp.platformUser.companyCapital")}
                hookForm={hookForm}
                disabled={disabled}
              />
            )}
          </Splitter>
          <Field
            required
            name="company"
            label={translate("myTourApp.platformUser.company")}
            hookForm={hookForm}
            disabled={disabled}
          />
          <Splitter width1={30} width2={68}>
            <Field
              required
              name="apeCode"
              label={translate("myTourApp.platformUser.apeCode")}
              hookForm={hookForm}
              disabled={disabled}
            />
            <Field
              required
              name="apeLabel"
              label={translate("myTourApp.platformUser.apeLabel")}
              hookForm={hookForm}
              disabled={disabled}
            />
          </Splitter>
        </>
      )}
    </>
  );
}

/**
 * The auto-fill engine: debounced lookup, the guided-flow state machine, the
 * rules that apply a result to the form, and the purge of stale auto-fill when
 * the SIRET changes. Returns only what the rendering needs.
 */
function useStep1SiretAutofill(hookForm: UseFormReturn<Step1Form>) {
  const {watch, setValue, getValues} = hookForm;
  const siret = watch("siret");
  const debouncedSiret = useDebouncedSiret(siret, SIRET_DEBOUNCE_MS);
  const {siretLookup, isSiretLookupFetching} = useSiretLookup(debouncedSiret, true);

  // Remembers the value we last auto-filled into each text field. A field is
  // (re)writable when it is empty OR still holds our previous auto-fill — so a
  // corrected SIRET replaces stale auto-fill, but a value the user typed is
  // never clobbered.
  const autofilledRef = useRef<AutofilledFields>({});
  /**
   * companyType ne peut pas suivre cette règle : son défaut ("") est indissociable
   * d'un champ vidé par l'utilisateur. On trace donc plutôt si l'utilisateur a
   * explicitement choisi une forme juridique — tant que non, le lookup peut la poser/remplacer.
   */
  const companyTypeTouchedRef = useRef(false);
  // The SIRET whose result currently populates the auto-filled fields, so we can
  // detect when it goes stale.
  const filledSiretRef = useRef<string | null>(null);

  // Guided-flow gates: the brief success flash has elapsed / the user accepted a
  // problem (not found, radiée) and wants to continue.
  const [successFlashDone, setSuccessFlashDone] = useState(false);
  const [problemAcknowledged, setProblemAcknowledged] = useState(false);

  // Purge stale auto-fill when the looked-up SIRET changes, so a corrected or
  // cleared SIRET never leaves the previous company's data behind. Declared
  // before the apply effect so a same-commit SIRET change clears first.
  useEffect(() => {
    if (filledSiretRef.current !== null && filledSiretRef.current !== debouncedSiret) {
      clearStaleAutofill();
      filledSiretRef.current = null;
    }
  }, [debouncedSiret]);

  // Apply a successful lookup once per result.
  useEffect(() => {
    if (siretLookup?.found !== true) {
      return;
    }
    applyLookupToForm(siretLookup.data);
    filledSiretRef.current = debouncedSiret;
  }, [siretLookup, debouncedSiret]);

  // A new SIRET restarts the guided flow. `debouncedSiret` is a trigger-only
  // dependency: the body just resets state, but the effect must re-run on every
  // SIRET change (otherwise a problem acknowledged for SIRET A would carry over).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional trigger-only dependency
  useEffect(() => {
    setSuccessFlashDone(false);
    setProblemAcknowledged(false);
  }, [debouncedSiret]);

  const siretFormatValid = SIRET_FORMAT.test(siret ?? "");
  // A valid SIRET is typed but its result isn't applied yet: debounce pending or
  // the network request in flight.
  const isLookingUp = siretFormatValid && (debouncedSiret !== siret || isSiretLookupFetching);
  const matchedName =
    siretLookup?.found === true && !siretLookup.data.isClosed ? siretLookup.data.legalName : null;
  const isClosedCompany = siretLookup?.found === true && siretLookup.data.isClosed;
  const isNotFound = siretLookup?.found === false;

  // Hold the success flash briefly, then let the fields appear.
  useEffect(() => {
    if (isLookingUp || matchedName === null || successFlashDone) {
      return;
    }
    const timer = setTimeout(() => setSuccessFlashDone(true), SUCCESS_FLASH_MS);
    return () => clearTimeout(timer);
  }, [isLookingUp, matchedName, successFlashDone]);

  return {
    phase: resolvePhase(),
    matchedName,
    problemKind: isClosedCompany
      ? ("closed" as const)
      : isNotFound
        ? ("not-found" as const)
        : null,
    acknowledgeProblem: () => setProblemAcknowledged(true),
    markCompanyTypePicked: () => {
      companyTypeTouchedRef.current = true;
    },
  };

  function resolvePhase(): SiretAutofillPhase {
    if (!siretFormatValid) {
      return "prompt";
    }
    if (isLookingUp) {
      return "loading";
    }
    if (matchedName !== null) {
      return successFlashDone ? "revealed" : "success";
    }
    if (isNotFound || isClosedCompany) {
      return problemAcknowledged ? "revealed" : "problem";
    }
    return "loading";
  }

  // Text fields are written only when safe; companyType is set unless the user
  // explicitly picked a legal form. `vatId`/address are not part of Step1 (#158).
  function applyLookupToForm(data: SiretLookupResult) {
    handleFill("company", data.legalName);
    // The lookup returns the NAF/APE code in DINUM's dotted form ("62.01Z"), but
    // company_legal_setting.ape_code is varchar(5) with dots stripped — fill the
    // canonical 5-char value so the step submits without truncation.
    handleFill("apeCode", normalizeApeCode(data.apeCode));
    if (data.apeLabel) {
      handleFill("apeLabel", data.apeLabel);
    }
    if (data.companyType && !companyTypeTouchedRef.current) {
      setValue("companyType", data.companyType, {shouldValidate: true, shouldDirty: true});
    }
  }

  function handleFill<K extends keyof AutofilledFields>(name: K, value: Step1Form[K]) {
    const current = getValues(name);
    const isUserValue = !isEmpty(current) && current !== autofilledRef.current[name];
    if (isUserValue) {
      return;
    }
    // `value` is correctly typed `Step1Form[K]`, but RHF's setValue cannot prove
    // its PathValue<> equals that for a generic key K — hence the cast.
    setValue(name, value as never, {shouldValidate: true, shouldDirty: true});
    autofilledRef.current[name] = value;
  }

  // Resets only what we auto-filled and the user has not since edited: text
  // fields back to empty, companyType back to its default (when not user-picked).
  function clearStaleAutofill() {
    for (const name of Object.keys(autofilledRef.current) as (keyof AutofilledFields)[]) {
      if (
        autofilledRef.current[name] !== undefined &&
        getValues(name) === autofilledRef.current[name]
      ) {
        setValue(name, "" as never, {shouldValidate: true, shouldDirty: true});
      }
    }
    autofilledRef.current = {};
    if (!companyTypeTouchedRef.current) {
      setValue("companyType", DEFAULT_COMPANY_TYPE, {shouldValidate: true, shouldDirty: true});
    }
  }
}

// The text fields the lookup can auto-fill, derived from the form type.
type AutofilledFields = Partial<Pick<Step1Form, "company" | "apeCode" | "apeLabel">>;

function isEmpty(value: unknown): boolean {
  return value == null || (typeof value === "string" && value.trim() === "");
}

// Strips separators/whitespace from a NAF/APE code ("62.01Z" -> "6201Z") to
// match the stored varchar(5) format.
function normalizeApeCode(apeCode: string): string {
  return apeCode.replace(/[\s.]/g, "");
}

/**
 * Debounces the raw SIRET input so the lookup only fires once the user stops
 * typing. Returns an empty string until the first debounce settles.
 */
function useDebouncedSiret(siret: string | undefined, delayMs: number): string {
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(siret ?? ""), delayMs);
    return () => clearTimeout(timer);
  }, [siret, delayMs]);

  return debounced;
}
